import { Button, DialogContent, DialogTitle } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers-pro";
import { isBefore, isValid, parseISO, startOfToday } from "date-fns";
import { useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import DialogButtons from "@entrilia/shared/components/DialogButtons";
import DialogCloseButton from "@entrilia/shared/components/DialogCloseButton";
import SmallDialog from "@entrilia/shared/components/SmallDialog";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import { logError } from "@entrilia/shared/logging";
import { formatDateOnly } from "@entrilia/shared/utilities/dateUtils";
import { defined } from "@entrilia/shared/utilities/typeHelper";
import adminApi from "../../../../api/adminApi";
import type { DataCollectionRequestDetails, RequestSubmissionInfo } from "../../../../api/types/dataCollectionTypes";

interface Props {
  dataRequestId: string;
  submissions: RequestSubmissionInfo[];
  onClose: () => void;
  onApply: (dataRequest: DataCollectionRequestDetails) => void;
}

const updateDataRequestSubmissions = withErrorHandling(adminApi.updateDataRequestSubmissions);

const ChangeSubmissionsDueDateDialog = ({ dataRequestId, submissions, onClose, onApply }: Props) => {
  const { sendNotification, sendNotificationError } = useNotificationContext();

  const [isSaving, setIsSaving] = useState(false);

  const [dueDate, setDueDate] = useState<Date | null>(() => parseISO(defined(submissions[0]).dueDate));

  const handleApply = async () => {
    if (!dueDate) {
      return;
    }

    setIsSaving(true);

    const [dataRequest, error] = await updateDataRequestSubmissions(dataRequestId, {
      submissionIds: submissions.map((s) => s.id),
      dueDate: formatDateOnly(dueDate),
    });

    setIsSaving(false);

    if (error) {
      logError(error, "[ChangeSubmissionsDueDateDialog] updateDataRequestSubmissions");
      sendNotificationError(error.message);
      return;
    }

    sendNotification("Due date updated successfully");
    onApply(dataRequest);
  };

  const handleDueDateChange = (date: Date | null) => {
    setDueDate(date);
  };

  const title =
    submissions.length === 1
      ? `Change Due Date for ${defined(submissions[0]).recipientName}`
      : `Change Due Date for ${submissions.length} companies`;

  const isDueDateValid = dueDate && isValid(dueDate) && !isBefore(dueDate, startOfToday());

  return (
    <SmallDialog open onClose={onClose}>
      <DialogTitle>{title}</DialogTitle>
      <DialogCloseButton onClick={onClose} />

      <DialogContent>
        <DatePicker
          disablePast
          value={dueDate}
          onChange={handleDueDateChange}
          slotProps={{
            textField: {
              fullWidth: true,
              label: "Due Date",
              error: !isDueDateValid,
            },
          }}
        />
      </DialogContent>

      <DialogButtons onCancel={onClose}>
        <Button variant="contained" disabled={!isDueDateValid} loading={isSaving} onClick={handleApply}>
          Save
        </Button>
      </DialogButtons>
    </SmallDialog>
  );
};

export default ChangeSubmissionsDueDateDialog;
